import type {CopilotModel} from '@github-ui/copilot-chat-types'
import {useCallback, useMemo, useSyncExternalStore} from 'react'

type ModelPromo = NonNullable<NonNullable<CopilotModel['billing']>['promo']>

const MAX_TIMEOUT_MS = 2_147_483_647
const NOOP = () => {}
const UTC_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/
const PROMO_END_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
})
const getServerSnapshot = () => false

function getPromoExpiry(endsAt: string | undefined): number | undefined {
  if (!endsAt) return undefined
  const match = UTC_TIMESTAMP_PATTERN.exec(endsAt)
  if (!match) return undefined

  const expiry = Date.parse(endsAt)
  if (!Number.isFinite(expiry)) return undefined

  const parsed = new Date(expiry)
  const [, year, month, day, hour, minute, second] = match
  const matchesParsedDate =
    parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() + 1 === Number(month) &&
    parsed.getUTCDate() === Number(day) &&
    parsed.getUTCHours() === Number(hour) &&
    parsed.getUTCMinutes() === Number(minute) &&
    parsed.getUTCSeconds() === Number(second)

  return matchesParsedDate ? expiry : undefined
}

function formatPromoEndDate(expiry: number): string {
  return PROMO_END_DATE_FORMATTER.format(expiry)
}

export function useActiveModelPromo(promo: ModelPromo | undefined): ModelPromo | undefined {
  const expiry = getPromoExpiry(promo?.ends_at)
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (expiry === undefined || expiry <= Date.now()) return NOOP

      let timeout: ReturnType<typeof setTimeout> | undefined
      const scheduleExpiry = () => {
        const remaining = expiry - Date.now()
        if (remaining <= 0) {
          onStoreChange()
          return
        }

        timeout = setTimeout(scheduleExpiry, Math.min(remaining, MAX_TIMEOUT_MS))
      }

      scheduleExpiry()
      return () => {
        if (timeout !== undefined) clearTimeout(timeout)
      }
    },
    [expiry],
  )
  const getSnapshot = useCallback(() => {
    if (expiry === undefined) return false
    return expiry > Date.now()
  }, [expiry])
  const isActive = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  return useMemo(() => {
    if (!isActive || !promo?.ends_at || expiry === undefined) return undefined
    if (!promo.message) return promo

    const baseMessage = promo.message.trimEnd()
    const terminalPunctuation = baseMessage.match(/[.!?]$/)?.[0] ?? ''
    const messageWithoutPunctuation = terminalPunctuation ? baseMessage.slice(0, -1).trimEnd() : baseMessage
    return {
      ...promo,
      message: `${messageWithoutPunctuation} until ${formatPromoEndDate(expiry)}${terminalPunctuation}`,
    }
  }, [expiry, isActive, promo])
}
